import React from 'react';
import { useTranslation } from 'react-i18next';

interface ProcessingButtonsProps {
  /** When true, the buttons are hidden and a single loading spinner is shown. */
  processing: boolean;
  /** The action buttons (e.g. Validate / Invalidate, Approve / Reject) shown when idle. */
  children: React.ReactNode;
}

/**
 * Wraps a group of action buttons where only one should run at a time. While an
 * action is processing it hides all the buttons and shows a single spinner, so
 * the user sees one clear "working" state instead of every button spinning and
 * the buttons cannot be clicked together.
 *
 * When idle it renders the buttons unchanged (as a fragment) so the surrounding
 * inline layout is preserved.
 */
export const ProcessingButtons = ({ processing, children }: ProcessingButtonsProps) => {
  const { t } = useTranslation();

  if (processing) {
    return (
      <span className="spinner-border text-primary align-middle" role="status" aria-live="polite">
        <span className="visually-hidden">{t('loading')}</span>
      </span>
    );
  }

  return <>{children}</>;
};

export default ProcessingButtons;
